import { motion } from "framer-motion";
import { Users, User, Map, CalendarCheck } from "lucide-react";

const sessions = [
  {
    step: "Session 01",
    title: "Orientation Session",
    audience: "Student + Parents",
    duration: "60 mins • Virtual",
    details: "Setting the purpose together. Parents and students understand how research based career exploration works — what you can expect from this program.",
    icon: Users,
    testid: "program-card-session-1",
  },
  {
    step: "Session 02",
    title: "Discovery & Debrief",
    audience: "1-to-1 with the Student",
    duration: "75 mins • Virtual",
    details: "A warm, in-depth walkthrough of the psychometric report — decoding personality dimensions, interest clusters and the student's core drivers.",
    icon: User,
    testid: "program-card-session-2",
  },
  {
    step: "Session 03",
    title: "Career Choice Mapping",
    audience: "1-to-1 with the Student",
    duration: "90 mins • Virtual",
    details: "Translating insights into action — modern career pathways, subject stream recommendations and high-growth opportunities mapped to the student's passions.",
    icon: Map,
    testid: "program-card-session-3",
  },
  {
    step: "Year-Long Care",
    title: "4 Quarterly Check-ins",
    audience: "1-to-1 • Optional, over 12 months",
    duration: "Once per Quarter",
    details: "Exploration doesn't end with a report. We answer emerging questions, brainstorm course choices and keep the momentum alive through the year.",
    icon: CalendarCheck,
    testid: "program-card-checkins",
  },
];

export default function Program() {
  return (
    <section id="program" className="py-20 sm:py-28" data-testid="program-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="max-w-2xl mb-14"
        >
          <p className="text-xs font-mono uppercase tracking-[0.25em] text-primary font-semibold mb-4">How It Works</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            One student. One journey. <span className="text-accent">Informed Choices.</span>
          </h2>
          <p className="mt-4 text-muted-foreground text-base sm:text-lg leading-relaxed">
            Every session is personal, virtual and exploratory. We begin with the parents, then walk one-on-one with the student — from discovery to meaningful interpretations.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {sessions.map((s, i) => (
            <motion.div
              key={s.testid}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.12 }}
              whileHover={{ y: -8 }}
              className="bg-card border border-border rounded-3xl p-7 flex flex-col shadow-sm hover:shadow-xl hover:shadow-primary/10 transition-shadow"
              data-testid={s.testid}
            >
              <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-5">
                <s.icon className="w-6 h-6" />
              </div>
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-accent font-semibold">{s.step}</p>
              <h3 className="font-display text-xl font-bold mt-2">{s.title}</h3>
              <p className="text-xs font-semibold text-primary mt-1.5">{s.audience}</p>
              <p className="text-xs text-muted-foreground font-mono mt-1">{s.duration}</p>
              <p className="text-sm text-muted-foreground leading-relaxed mt-4">{s.details}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
